import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Launch as LaunchIcon,
  DataObject as DataIcon,
  Close as CloseIcon,
  Language as WebsiteIcon,
} from '@mui/icons-material';
import { dataAPI } from '../../services/api';
import toast from 'react-hot-toast';

const WebsiteDataDialog = ({ open, onClose, websites }) => {
  const [loading, setLoading] = useState(false);
  const [websitesData, setWebsitesData] = useState({});
  const [error, setError] = useState(null);
  const [format, setFormat] = useState('detailed');
  const [selectedWebsiteTab, setSelectedWebsiteTab] = useState(0);

  useEffect(() => {
    if (open && websites && websites.length > 0) {
      fetchAllWebsitesData();
    }
  }, [open, websites, format]);

  const fetchAllWebsitesData = async () => {
    try {
      setLoading(true);
      setError(null);
      const allData = {};

      // Fetch data for each website
      for (const website of websites) {
        try {
          const response = await dataAPI.getWebsiteData(website._id, { format });
          allData[website._id] = response.data;
        } catch (error) {
          console.error(`Error fetching data for website ${website.name}:`, error);
          allData[website._id] = {
            error: error.response?.data?.message || 'Failed to fetch data',
            website: website
          };
        }
      }

      setWebsitesData(allData);
    } catch (error) {
      console.error('Error fetching websites data:', error);
      setError('Failed to fetch websites data');
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (value, fieldType) => {
    if (value === null || value === undefined) return '-';
    
    switch (fieldType) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'array':
        if (Array.isArray(value)) {
          return value.length <= 3 
            ? value.join(', ') 
            : `${value.slice(0, 3).join(', ')} +${value.length - 3} more`;
        }
        return JSON.stringify(value);
      case 'object':
        return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
      default:
        return String(value);
    }
  };

  const copyApiEndpoint = (websiteId) => {
    const endpoint = `${window.location.origin}/api/data/website/${websiteId}`;
    navigator.clipboard.writeText(endpoint);
    toast.success('API endpoint copied to clipboard');
  };

  const copyPublicEndpoint = (siteId) => {
    const endpoint = `${window.location.origin}/api/data/site/${siteId}`;
    navigator.clipboard.writeText(endpoint);
    toast.success('Public API endpoint copied to clipboard');
  };

  const openApiInNewTab = (websiteId) => {
    const endpoint = `${window.location.origin}/api/data/website/${websiteId}`;
    window.open(endpoint, '_blank');
  };

  const copyAllWebsitesEndpoint = () => {
    const endpoints = websites.map(website => 
      `${window.location.origin}/api/data/website/${website._id}`
    ).join('\n');
    navigator.clipboard.writeText(endpoints);
    toast.success('All API endpoints copied to clipboard');
  };

  const getTotalStats = () => {
    let totalSchemas = 0;
    let totalRecords = 0;

    Object.values(websitesData).forEach(data => {
      if (data && !data.error) {
        totalSchemas += data.schemas?.length || 0;
        totalRecords += data.pagination?.total || 0;
      }
    });

    return { totalSchemas, totalRecords };
  };

  if (!websites || websites.length === 0) return null;

  const { totalSchemas, totalRecords } = getTotalStats();
  const currentWebsite = websites[selectedWebsiteTab];
  const currentWebsiteData = websitesData[currentWebsite?._id];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={{ xs: true, sm: false }}
      PaperProps={{
        sx: { 
          minHeight: { xs: '100vh', sm: '80vh' }, 
          maxHeight: { xs: '100vh', sm: '95vh' },
          m: { xs: 0, sm: 3 }
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" component="div">
              All Websites Data
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {websites.length} websites • {totalSchemas} schemas • {totalRecords} total records
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Copy All API Endpoints">
              <IconButton size="small" onClick={copyAllWebsitesEndpoint}>
                <CopyIcon />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box>
            {/* Website Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={selectedWebsiteTab} 
                onChange={(e, newValue) => setSelectedWebsiteTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  '& .MuiTab-root': {
                    minWidth: { xs: 120, sm: 160 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }
                }}
              >
                {websites.map((website, index) => {
                  const websiteData = websitesData[website._id];
                  const schemaCount = websiteData?.schemas?.length || 0;
                  const recordCount = websiteData?.pagination?.total || 0;
                  
                  return (
                    <Tab 
                      key={website._id}
                      label={
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle2">
                            {website.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {schemaCount} schemas • {recordCount} records
                          </Typography>
                        </Box>
                      }
                      icon={<WebsiteIcon />}
                      iconPosition="start"
                    />
                  );
                })}
              </Tabs>
            </Box>

            {/* Current Website Content */}
            {currentWebsiteData ? (
              currentWebsiteData.error ? (
                <Alert severity="error">
                  Error loading data for {currentWebsite.name}: {currentWebsiteData.error}
                </Alert>
              ) : (
                <Box>
                  {/* API Information */}
                  <Card sx={{ mb: 3 }}>
                    <CardHeader 
                      title={`API Endpoints for ${currentWebsite.name}`}
                      titleTypographyProps={{ variant: 'h6' }}
                    />
                    <CardContent>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          By Website ID:
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'stretch', sm: 'center' }, 
                          gap: 1, 
                          mb: 2 
                        }}>
                          <Typography variant="body2" sx={{ 
                            fontFamily: 'monospace', 
                            bgcolor: 'grey.100', 
                            p: 1, 
                            borderRadius: 1,
                            flexGrow: 1,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            wordBreak: 'break-all'
                          }}>
                            GET /api/data/website/{currentWebsite._id}
                          </Typography>
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            flexShrink: 0
                          }}>
                            <Button size="small" onClick={() => copyApiEndpoint(currentWebsite._id)}>
                              Copy
                            </Button>
                            <Button size="small" onClick={() => openApiInNewTab(currentWebsite._id)}>
                              Open
                            </Button>
                          </Box>
                        </Box>
                        
                        <Typography variant="subtitle2" gutterBottom>
                          By Site ID (Public):
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'stretch', sm: 'center' }, 
                          gap: 1 
                        }}>
                          <Typography variant="body2" sx={{ 
                            fontFamily: 'monospace', 
                            bgcolor: 'grey.100', 
                            p: 1, 
                            borderRadius: 1,
                            flexGrow: 1,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            wordBreak: 'break-all'
                          }}>
                            GET /api/data/site/{currentWebsite.siteId}
                          </Typography>
                          <Button 
                            size="small" 
                            onClick={() => copyPublicEndpoint(currentWebsite.siteId)}
                            sx={{ flexShrink: 0 }}
                          >
                            Copy
                          </Button>
                        </Box>
                      </Box>
                      
                      <Typography variant="caption" color="textSecondary">
                        Query parameters: format=simple|detailed, status=published|draft|all, limit, page
                      </Typography>
                    </CardContent>
                  </Card>

                  {/* Website Summary */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between', 
                        alignItems: { xs: 'stretch', sm: 'center' }, 
                        gap: 2,
                        mb: 2 
                      }}>
                        <Typography variant="h6">
                          {currentWebsiteData.website.name}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'row', sm: 'row' },
                          gap: 1,
                          justifyContent: { xs: 'center', sm: 'flex-end' }
                        }}>
                          <Chip label={`${currentWebsiteData.schemas.length} schemas`} size="small" />
                          <Chip label={`${currentWebsiteData.pagination.total} records`} color="primary" size="small" />
                        </Box>
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        Site ID: {currentWebsiteData.website.siteId}
                      </Typography>
                    </CardContent>
                  </Card>

                  {/* Schema Data */}
                  {currentWebsiteData.schemas.length === 0 ? (
                    <Alert severity="info">
                      No active schemas found for this website.
                    </Alert>
                  ) : (
                    currentWebsiteData.schemas.map((schemaInfo) => {
                      const schemaData = currentWebsiteData.data[schemaInfo.slug];
                      
                      return (
                        <Accordion key={schemaInfo.id} defaultExpanded>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'flex-start', sm: 'center' }, 
                              gap: { xs: 1, sm: 2 }, 
                              width: '100%' 
                            }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                minWidth: 0,
                                flex: 1
                              }}>
                                <DataIcon />
                                <Typography variant="h6" sx={{ 
                                  fontSize: { xs: '1rem', sm: '1.25rem' },
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {schemaInfo.name}
                                </Typography>
                              </Box>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                flexShrink: 0
                              }}>
                                <Chip 
                                  label={`${schemaInfo.recordCount} records`} 
                                  size="small" 
                                  color="primary"
                                />
                                <Typography 
                                  variant="caption" 
                                  color="textSecondary" 
                                  sx={{ 
                                    display: { xs: 'block', sm: 'block' },
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                  }}
                                >
                                  /{schemaInfo.slug}
                                </Typography>
                              </Box>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {format === 'simple' ? (
                              <Box>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                  Simple format - data only:
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                  <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                                    {JSON.stringify(schemaData, null, 2)}
                                  </pre>
                                </Paper>
                              </Box>
                            ) : schemaData && schemaData.records && schemaData.records.length > 0 ? (
                              <>
                                {/* Mobile Card View */}
                                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                  {schemaData.records.map((record) => (
                                    <Card key={record.id} sx={{ mb: 2 }} variant="outlined">
                                      <CardContent sx={{ pb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                          <Chip 
                                            label={record.status} 
                                            size="small"
                                            color={record.status === 'published' ? 'success' : 'default'}
                                          />
                                          <Typography variant="caption" color="textSecondary">
                                            {new Date(record.createdAt).toLocaleDateString()}
                                          </Typography>
                                        </Box>
                                        {schemaData.schema.fields.slice(0, 3).map((field) => (
                                          <Box key={field.name} sx={{ mb: 1 }}>
                                            <Typography variant="caption" color="textSecondary">
                                              {field.label}:
                                            </Typography>
                                            <Typography variant="body2" sx={{ ml: 1 }}>
                                              {formatValue(record.data[field.name], field.type)}
                                            </Typography>
                                          </Box>
                                        ))}
                                        {schemaData.schema.fields.length > 3 && (
                                          <Typography variant="caption" color="textSecondary">
                                            +{schemaData.schema.fields.length - 3} more fields
                                          </Typography>
                                        )}
                                      </CardContent>
                                    </Card>
                                  ))}
                                </Box>

                                {/* Desktop Table View */}
                                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                  <TableContainer component={Paper}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          {schemaData.schema.fields.slice(0, 4).map((field) => (
                                            <TableCell key={field.name}>
                                              <Box>
                                                <Typography variant="subtitle2">
                                                  {field.label}
                                                </Typography>
                                                <Chip label={field.type} size="small" variant="outlined" />
                                              </Box>
                                            </TableCell>
                                          ))}
                                          <TableCell>Status</TableCell>
                                          <TableCell>Created</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {schemaData.records.map((record) => (
                                          <TableRow key={record.id} hover>
                                            {schemaData.schema.fields.slice(0, 4).map((field) => (
                                              <TableCell key={field.name}>
                                                <Typography variant="body2" sx={{ 
                                                  maxWidth: 200,
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap'
                                                }}>
                                                  {formatValue(record.data[field.name], field.type)}
                                                </Typography>
                                              </TableCell>
                                            ))}
                                            <TableCell>
                                              <Chip 
                                                label={record.status} 
                                                size="small"
                                                color={record.status === 'published' ? 'success' : 'default'}
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {new Date(record.createdAt).toLocaleDateString()}
                                              </Typography>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Box>
                              </>
                            ) : (
                              <Alert severity="info">
                                No records found for this schema.
                              </Alert>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                  )}
                </Box>
              )
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 1 },
        p: { xs: 2, sm: 1 }
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1, 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          flexGrow: 1,
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Typography 
            variant="body2" 
            color="textSecondary"
            sx={{ 
              textAlign: { xs: 'center', sm: 'left' },
              mb: { xs: 1, sm: 0 }
            }}
          >
            Format:
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            justifyContent: { xs: 'center', sm: 'flex-start' }
          }}>
            <Button
              variant={format === 'detailed' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setFormat('detailed')}
              fullWidth={{ xs: true, sm: false }}
            >
              Detailed
            </Button>
            <Button
              variant={format === 'simple' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setFormat('simple')}
              fullWidth={{ xs: true, sm: false }}
            >
              Simple
            </Button>
          </Box>
        </Box>
        <Button 
          onClick={onClose}
          fullWidth={{ xs: true, sm: false }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WebsiteDataDialog; 